import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOCATIONS = ["Kerala, India", "Dubai, UAE", "London, UK", "Other"];

export default function Enquiry() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    wedding_date: "",
    location: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please add your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSuccess(true);
      toast.success("Your enquiry is with us. We'll be in touch within 24 hours.");
      setForm({
        name: "",
        email: "",
        phone: "",
        wedding_date: "",
        location: "",
        message: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 focus:border-[#C4A484] text-[#FDFBF7] py-3 font-outfit text-base placeholder:text-[#8F8F8F]/60 transition-colors duration-500";

  return (
    <section
      id="enquire"
      data-testid="enquiry-section"
      className="relative bg-[#050505] py-24 md:py-40 border-t border-white/10"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-10">
          <span className="font-mono-label text-[10px] text-[#C4A484]">
            005 · Reserve Your Date
          </span>
          <span className="flex-1 h-px bg-white/10" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="md:col-span-5"
          >
            <h2 className="font-heading text-5xl md:text-6xl tracking-tighter leading-[0.95] mb-8">
              Tell us about
              <br />
              your <span className="italic text-[#C4A484]">day.</span>
            </h2>
            <p className="text-[#8F8F8F] text-sm leading-relaxed mb-10 max-w-md">
              We shoot a limited number of weddings each season. Send a note and
              we'll reply within 24 hours with availability, sample films, and
              pricing for your date. We&apos;ll take it from there.
            </p>
            <div className="space-y-4 font-mono-label text-[11px] text-[#FDFBF7]/70">
              <div>
                <span className="text-[#C4A484]">→ EMAIL </span>
                hello@flyoraweddings.com
              </div>
              <div>
                <span className="text-[#C4A484]">→ WHATSAPP </span>
                +91 97471 17777
              </div>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.15 }}
            viewport={{ once: true }}
            onSubmit={submit}
            data-testid="enquiry-form"
            className="md:col-span-7 space-y-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="font-mono-label text-[10px] text-[#8F8F8F] block mb-2">
                  Your Name *
                </label>
                <input
                  data-testid="enquiry-name"
                  className={inputCls}
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Full name"
                  required
                />
              </div>
              <div>
                <label className="font-mono-label text-[10px] text-[#8F8F8F] block mb-2">
                  Email *
                </label>
                <input
                  data-testid="enquiry-email"
                  type="email"
                  className={inputCls}
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div>
                <label className="font-mono-label text-[10px] text-[#8F8F8F] block mb-2">
                  Phone / WhatsApp *
                </label>
                <input
                  data-testid="enquiry-phone"
                  className={inputCls}
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="+91 · +971 · +44"
                  required
                />
              </div>
              <div>
                <label className="font-mono-label text-[10px] text-[#8F8F8F] block mb-2">
                  Wedding Date
                </label>
                <input
                  data-testid="enquiry-date"
                  type="date"
                  className={inputCls}
                  value={form.wedding_date}
                  onChange={(e) => update("wedding_date", e.target.value)}
                />
              </div>
              <div>
                <label className="font-mono-label text-[10px] text-[#8F8F8F] block mb-2">
                  Location
                </label>
                <select
                  data-testid="enquiry-location"
                  className={`${inputCls} appearance-none cursor-pointer`}
                  value={form.location}
                  onChange={(e) => update("location", e.target.value)}
                >
                  <option value="" className="bg-[#0F0F11]">
                    Select
                  </option>
                  {LOCATIONS.map((l) => (
                    <option key={l} value={l} className="bg-[#0F0F11]">
                      {l}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="font-mono-label text-[10px] text-[#8F8F8F] block mb-2">
                Tell us your story
              </label>
              <textarea
                data-testid="enquiry-message"
                className={`${inputCls} resize-none`}
                rows="3"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="Venue, number of days, films you loved from our portfolio..."
              />
            </div>

            <div className="flex flex-col md:flex-row md:items-center gap-6 pt-4">
              <button
                type="submit"
                data-testid="enquiry-submit"
                disabled={submitting}
                className="font-mono-label text-[11px] px-10 py-4 border border-[#C4A484] text-[#C4A484] hover:bg-[#C4A484] hover:text-black transition-all duration-500 disabled:opacity-50 flex items-center justify-center gap-3 group"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Sending...
                  </>
                ) : success ? (
                  "Thank You →"
                ) : (
                  "Send Enquiry →"
                )}
              </button>
              <span className="text-[#8F8F8F] text-xs">
                We reply within 24 hours. Your details stay private.
              </span>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
